/**
 * WHICH «بازی دوباره» EACH SEAT GETS — the one decision, so the container and every one of the
 * nine end-of-match overlays agree without each re-deriving it.
 *
 * Operator, 2026-09-09: on a PRIVATE table the rematch is the host's call. It appears for the
 * host first and only becomes active for everyone else once the host has pressed it.
 *
 * Operator, session B (2026-10-08): «a rematch button shows only when it can work». A private
 * rematch can work iff our socket to the finished room is alive, the OWNER — whoever holds the
 * crown — is still at the table, and at least one other human of the match is still there (or the
 * match had no other human at all, e.g. the owner against bots). Otherwise the button is HIDDEN and
 * one line says why. Explore `02-rematch.md` R4–R8, spec item 2.
 *
 * Operator, card-a5 item 8 (2026-10-11): «Rematch after the owner drops: keep it simple — the
 * crown passes to a guest after the owner's reconnect grace, as before.» Session B froze the owner
 * at the match's end; the crown moves after it again, and a guest it lands on (an HEIR) is the
 * owner here — asked the same «is anybody else still here» question as any owner.
 *
 * ⚠️ THE SERVER OWNS THE AUTHORIZATION, NOT THIS. `OnlineRoom`'s `rematch` handler re-checks
 * host-only / private-only / terminal-only, so a crafted client that skips this gains nothing —
 * the same split as «اخراج». This decides only what the player is offered.
 *
 * ⚠️ PUBLIC IS DELIBERATELY NOT A REMATCH: still an ordinary re-queue, for everyone, immediately
 * — a fresh QUICK-MATCH of this game whatever door the player came in by (the container builds
 * it). A public rematch needs both sides to opt in, a way to decline, and a window on the offer;
 * none of that is built (operator: «I'd rather see the private one working first»).
 */

/**
 * CONTRACT C1 — the game-server's verdict on a PRIVATE table's rematch, sent right after the
 * terminal's `gameover` and again on every post-match presence change (leave, drop, return).
 * Public and solo tables never send it, and an OLD game-server never does: «never received» is
 * UNKNOWN, never «unavailable».
 */
export interface RematchStateFrame {
  readonly available: boolean;
  readonly reason: "ok" | "owner-left" | "others-left";
}

/** Why the button is not shown — each has its own line. */
export type RematchHiddenReason =
  /** The owner has left the finished table (the server said so, or the roster shows it). */
  | "owner-left"
  /** Every other human of the match has left — the owner would be alone. */
  | "others-left"
  /** Our own socket to the finished room has closed: nothing can be asked or heard any more. */
  | "connection"
  /** The offered successor is no longer joinable (asked before following). */
  | "successor";

export type RematchAction =
  /** Public: tear this connection down and quick-match again — what the button has always done. */
  | { readonly kind: "requeue"; readonly disabled: false }
  /** Private OWNER, before they have offered: ask the server to open the successor. */
  | { readonly kind: "offer"; readonly disabled: false }
  /** Private guest, before the owner has offered: shown, inert, and explained. */
  | { readonly kind: "wait"; readonly disabled: true }
  /** Anyone, once the offer is on the table: join that room, keeping our seat. */
  | { readonly kind: "join"; readonly disabled: false; readonly roomId: string }
  /** Private, and it cannot work: no button at all, one line saying why. */
  | { readonly kind: "hidden"; readonly disabled: true; readonly why: RematchHiddenReason };

const hidden = (why: RematchHiddenReason): RematchAction => ({
  kind: "hidden",
  disabled: true,
  why,
});

export function rematchAction(table: {
  /** Whether this is a private (host-owned) table — a public one is ownerless. */
  readonly isPrivate: boolean;
  /**
   * Whether WE hold the crown — the LIVE `isHost`, after the end too (card-a5 item 8). Both the old
   * game-server and today's hand it to a guest once the owner has gone (today's only when the
   * owner's reconnect grace has run out, or at once on a deliberate close), and that heir owns the
   * rematch. Session B froze this at the first `gameover`; that freeze is what left a guest's
   * button dead for good after one owner blip (the card-60 review's R2).
   */
  readonly isOwner: boolean;
  /** The successor the owner has opened, or null while there is no offer. */
  readonly offerRoomId: string | null;
  /** The server's verdict (contract C1), or `"unknown"` — never received (an old game-server). */
  readonly rematchState: RematchStateFrame | "unknown";
  /** Our socket to the finished room is still open. */
  readonly roomAlive: boolean;
  /** UNKNOWN fallback — nobody on the live roster wears the crown (the owner has gone, and no
   *  game-server handed it on). */
  readonly ownerSeatBotted: boolean;
  /** UNKNOWN fallback — every OTHER human of the match is gone, and there was at least one. */
  readonly everyOtherHumanBotted: boolean;
  /** The offered successor, asked just before following: `false` = no longer joinable. */
  readonly successorJoinable: boolean | "unknown";
}): RematchAction {
  if (!table.isPrivate) return { kind: "requeue", disabled: false };
  /*
   * AN OFFER OUTRANKS EVERY HIDE. Following needs neither the old socket nor the owner: the owner
   * auto-follows their own offer, which CLOSES their old socket — and the takeover that follows
   * (or a `rematch-state` owner-left) must not take the guests' door away. Only the successor's
   * own fate can.
   */
  if (table.offerRoomId !== null) {
    if (table.successorJoinable === false) return hidden("successor");
    return { kind: "join", disabled: false, roomId: table.offerRoomId };
  }
  // Nothing can be asked (the offer) or heard (the verdict, the offer) over a closed socket.
  if (!table.roomAlive) return hidden("connection");
  const owned = (): RematchAction =>
    table.isOwner ? { kind: "offer", disabled: false } : { kind: "wait", disabled: true };
  if (table.rematchState !== "unknown") {
    if (table.rematchState.available) return owned();
    return hidden(table.rematchState.reason === "others-left" ? "others-left" : "owner-left");
  }
  // UNKNOWN (an old game-server): today's behaviour, plus what the client can see for itself.
  if (!table.isOwner && table.ownerSeatBotted) return hidden("owner-left");
  if (table.everyOtherHumanBotted) return hidden(table.isOwner ? "others-left" : "owner-left");
  return owned();
}

/**
 * The `rematch-state` frame, shape-checked at the edge like every inbound frame — or null.
 *
 * Only the two CONSISTENT shapes pass: available with «ok», unavailable with a reason. A frame that
 * contradicts itself is a server bug, and acting on it would hide (or light) a button on a guess.
 */
export function readRematchState(msg: unknown): RematchStateFrame | null {
  if (typeof msg !== "object" || msg === null) return null;
  const { available, reason } = msg as { available?: unknown; reason?: unknown };
  if (available === true && reason === "ok") return { available: true, reason: "ok" };
  if (available === false && (reason === "owner-left" || reason === "others-left")) {
    return { available: false, reason };
  }
  return null;
}
